/**
 * 計算フィールドの「計算に使った値」の内訳を作る（#3358）。
 *
 * `IF(SUM({明細.金額}) = {合計金額}, "OK", "不一致")` のような式で
 * 「不一致」と出たとき、式を読まないと**なぜ**不一致なのか分からない。
 * 参照した項目とその値を並べて、結果画面のセル下に出す。
 *
 *   明細金額の合計：1,828,126 ≠ 小計：1,828,120
 *   合計請求金額：2,010,938 > 1,000
 *
 * 式は自由に書けるため「意味のある説明」を自動で作ることはできない。
 * 参照先の値（と比較相手の定数）を並べるだけに留める。
 *
 * 画面表示専用のため functions 側には複製を置かない。
 * 保存される値は applyCalculatedFields が決め、ここは表示だけを担う。
 */
import { relationSymbol, type ComparisonPart, type ComparisonTerm } from './calculationComparison';
import { parseNumericValue } from './calculationNumeric';

/** 内訳1件 */
export interface CalculationBreakdownEntry {
  /** フィールドコード。比較の項目と突き合わせるために使う */
  fieldCode: string;
  /** 表示名（項目名。明細の集計は「○○の合計」） */
  label: string;
  /** 表示する値。空値は空文字 */
  value: string;
}

/** 内訳を作るのに必要な項目の情報 */
export interface BreakdownItem {
  /** フィールドコード */
  fieldCode: string;
  /** 画面に出す項目名 */
  itemName: string;
  /** 明細の列ならその親項目のID。単一項目は undefined */
  parentId?: string;
}

/** 内訳を作るのに必要な値の取得方法 */
export interface BreakdownValueSource {
  /** 単一項目の値を取る（フィールドコード） */
  getSingleValue: (fieldCode: string) => string | undefined;
  /** 明細の列の全行の値を取る（フィールドコード） */
  getColumnValues: (fieldCode: string) => string[] | undefined;
}

/** 内訳の区切り */
export const BREAKDOWN_SEPARATOR = ' ／ ';

/** 項目名と値の区切り（#3412）。項目名と値の境目が分かるようにする */
export const LABEL_VALUE_SEPARATOR = '：';

/** 数値は3桁区切りにする（1828126 → 1,828,126）。桁数が読み取りやすくなる */
export const formatValue = (value: string): string => {
  if (value.trim() === '') return '(空欄)';
  const num = parseNumericValue(value);
  return num !== undefined ? num.toLocaleString('ja-JP') : value;
};

/** 合計の表示名（「金額の合計」の形にする） */
export const sumLabel = (itemName: string): string => `${itemName}の合計`;

/** 数値として足せる値だけを取り出す（SUM と同じ扱い。空値・非数値はスキップ） */
export const summableNumbers = (values: string[]): number[] =>
  values.flatMap(value => {
    const num = parseNumericValue(value);
    return num !== undefined ? [num] : [];
  });

/** 数値として足せる値だけを合計する */
export const sumValues = (values: string[]): number =>
  summableNumbers(values).reduce((total, num) => total + num, 0);

/**
 * 式が参照している項目と、その値の内訳を作る。
 *
 * @param formula 計算式
 * @param referencedCodes 式が参照しているフィールドコード（extractFieldCodes の結果）
 * @param items フィールドコードから項目を引くための一覧
 * @param source 値の取得方法
 */
export const buildCalculationBreakdown = (
  formula: string,
  referencedCodes: string[],
  items: BreakdownItem[],
  source: BreakdownValueSource,
): CalculationBreakdownEntry[] => {
  const byCode = new Map(items.map(item => [item.fieldCode, item]));
  const entries: CalculationBreakdownEntry[] = [];

  for (const code of referencedCodes) {
    const item = byCode.get(code);
    if (!item) continue;

    // 明細の列は SUM で合計されている前提で合計値を出す。
    // SUM 以外で明細の列を参照する式は単一項目からは書けない（行が決まらない）
    if (item.parentId !== undefined) {
      const values = source.getColumnValues(code);
      if (values === undefined) continue;
      entries.push({
        fieldCode: code,
        label: sumLabel(item.itemName),
        value: String(sumValues(values)),
      });
      continue;
    }

    const value = source.getSingleValue(code);
    entries.push({ fieldCode: code, label: item.itemName, value: value ?? '' });
  }

  return entries;
};

/**
 * 内訳を1行の文字列にする。
 *
 * 式の中の比較は、書かれた順に関係を記号で示す（#3412）。
 * 「明細金額の合計：1,828,126 ≠ 小計：1,828,120」
 * 「合計金額：41,000 < 100,000 ／ 合計金額：41,000 > 0」のように、
 * 何と何を比べてどうだったかが一目で分かる。
 *
 * 比較に入っていない項目は、その後ろに値を並べる。
 */
export const formatCalculationBreakdown = (
  entries: CalculationBreakdownEntry[],
  /** extractComparisons の結果 */
  comparisons: ComparisonPart[] = [],
): string => {
  const byCode = new Map(entries.map(entry => [entry.fieldCode, entry]));
  const labeled = (entry: CalculationBreakdownEntry): string =>
    `${entry.label}${LABEL_VALUE_SEPARATOR}${formatValue(entry.value)}`;

  // 比較の片側を「表示する文字列」と「関係を決める値」にする。項目一覧に無ければ出さない
  const resolve = (term: ComparisonTerm): { text: string; value: string } | undefined => {
    if (term.kind === 'literal') {
      // 文字列の定数は書かれたまま出す（"1000" を 1,000 にすると数値に見えるため）
      const text = term.isText && term.value !== '' ? term.value : formatValue(term.value);
      return { text, value: term.value };
    }
    const entry = byCode.get(term.fieldCode);
    return entry && { text: labeled(entry), value: entry.value };
  };

  const parts: string[] = [];
  const comparedCodes = new Set<string>();
  for (const { left, right } of comparisons) {
    const l = resolve(left);
    const r = resolve(right);
    if (l === undefined || r === undefined) continue;
    const equalityOnly = [left, right].some(term => term.kind === 'literal' && term.isText);
    parts.push(`${l.text} ${relationSymbol(l.value, r.value, equalityOnly)} ${r.text}`);
    [left, right].forEach(term => {
      if (term.kind === 'field') comparedCodes.add(term.fieldCode);
    });
  }

  entries
    .filter(entry => !comparedCodes.has(entry.fieldCode))
    .forEach(entry => parts.push(labeled(entry)));

  // 同じ比較を2回書いた式でも1回だけ出す
  return parts.filter((part, index) => parts.indexOf(part) === index).join(BREAKDOWN_SEPARATOR);
};
